import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm font-medium transition-colors duration-fast motion-reduce:transition-none focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)] ring-0 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 disabled:bg-surface-3 disabled:text-foreground-subtle disabled:hover:bg-surface-3 aria-disabled:bg-surface-3 aria-disabled:text-foreground-subtle",
        secondary: "bg-surface-2 text-foreground hover:bg-surface-3 active:bg-surface-1",
        outline: "border border-border bg-background text-foreground hover:bg-surface-1 active:bg-surface-2",
        ghost: "text-foreground hover:bg-surface-1 active:bg-surface-2",
        destructive: "bg-error text-primary-foreground hover:bg-error/90 active:bg-error/80",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render the child element with the button's styling instead of a `<button>`,
   * which is how a link is made to look like a button without nesting an anchor
   * inside one.
   */
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    // Harvested with `asChild` declared but never destructured, so it fell
    // through into the spread and React put `aschild="true"` on the DOM node —
    // the slot behaviour every call site asked for silently did nothing.
    const Component = asChild ? Slot : "button";
    return (
      <Component
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
